
import React, { useState, useContext, useRef } from 'react';
import { BiX, BiFile, BiDownload, BiCalendar, BiGroup, BiTrash, BiEdit, BiShow, BiLayer, BiSave, BiXCircle, BiUpload } from 'react-icons/bi';
import { useTranslation } from 'react-i18next';
import useAxiosPrivate from '../../../../../hooks/useAxiosPrivate';
import API_URLS from '../../../../../constants/apiConstants';
import AuthContext from "../../../../../context/AuthProvider";
import { DeleteConfirmationDialog } from '../../../../../components/components/js/DeleteConfirmationDialog';
import { toast } from 'react-toastify';

export const DocumentDetailsModal = ({ open, onClose, documentId, onDelete, onEdit }) => {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const axiosPrivate = useAxiosPrivate();
  const { school } = useContext(AuthContext);
  
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [availableRooms, setAvailableRooms] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteType, setDeleteType] = useState(null);
  const [fileToDeleteId, setFileToDeleteId] = useState(null);
  const [deletingFileId, setDeletingFileId] = useState(null);
  const fileInputRef = useRef(null);
  
  // Edit form state
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    target_audience: '',
    room_ids: [],
    newFiles: []
  });
  
  // Error states
  const [errors, setErrors] = useState({
    title: '',
    room_ids: ''
  });

  // Track original values to detect changes
  const [originalValues, setOriginalValues] = useState({
    title: '',
    description: '',
    target_audience: '',
    room_ids: []
  });

  const THEME = {
    primary: '#730595',
    softBg: '#fcfaff',
    textMain: '#222',
    textMuted: '#555',
    border: '#f0f0f0'
  };

  React.useEffect(() => {
    if (open && documentId) {
      setLoading(true);
      setIsEditMode(false);
      
      // Fetch document details and rooms in parallel
      Promise.all([
        axiosPrivate.get(API_URLS.getDocumentGroupDetails(documentId)),
        axiosPrivate.get(API_URLS.getSchoolRooms(school.id))
      ])
        .then(([docRes, roomsRes]) => {
          setDocument(docRes.data);
          setAvailableRooms(roomsRes.data || []);
          
          const initialData = {
            title: docRes.data.title,
            description: docRes.data.description || '',
            target_audience: docRes.data.target_audience,
            room_ids: docRes.data.rooms || [],
            newFiles: []
          };
          setEditForm(initialData);
          setOriginalValues({
            title: docRes.data.title,
            description: docRes.data.description || '',
            target_audience: docRes.data.target_audience,
            room_ids: docRes.data.rooms || []
          });
        })
        .catch(() => console.error('Error loading details'))
        .finally(() => setLoading(false));
    }
  }, [open, documentId, axiosPrivate, school.id]);

  // Reset all states when modal closes
  React.useEffect(() => {
    if (!open) {
      // Reset all delete-related states
      setShowDeleteConfirm(false);
      setDeleteType(null);
      setFileToDeleteId(null);
      setDeletingFileId(null);
      // Reset edit mode
      setIsEditMode(false);
      setErrors({ title: '', room_ids: '' });
    }
  }, [open]);

  React.useEffect(() => {
    const bodyElement = window.document.body; 
    
    if (open) {
      bodyElement.style.overflow = 'hidden';
    } else {
      bodyElement.style.overflow = 'unset';
    }

    return () => {
      bodyElement.style.overflow = 'unset';
    };
  }, [open]);

  const handleDeleteGroup = () => {
    setDeleteType('group');
    setShowDeleteConfirm(true);
  };
 
  const triggerDeleteFile = (fileId) => {
    if (document.documents && document.documents.length <= 1) {
      toast.error(isAr ? 'لا يمكن حذف الملف الوحيد' : 'Cannot delete the only file');
      return;
    }
    setDeleteType('file');
    setFileToDeleteId(fileId);
    setShowDeleteConfirm(true);
  };

  const handleDownload = async (url, name) => {
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const bUrl = window.URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = bUrl;
      a.download = name || 'file';
      a.click();
      window.URL.revokeObjectURL(bUrl);
    } catch { 
      window.open(url, '_blank'); 
    }
  };

  const handleEditClick = () => {
    setIsEditMode(true);
    setErrors({ title: '', room_ids: '' });
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setErrors({ title: '', room_ids: '' });
    setEditForm({
      title: originalValues.title,
      description: originalValues.description,
      target_audience: originalValues.target_audience,
      room_ids: originalValues.room_ids,
      newFiles: []
    });
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setEditForm(prev => ({
      ...prev,
      newFiles: [...prev.newFiles, ...files]
    }));
  };

  const handleRemoveNewFile = (index) => {
    setEditForm(prev => ({
      ...prev,
      newFiles: prev.newFiles.filter((_, i) => i !== index)
    }));
  };

  const handleRoomToggle = (roomId) => {
    setEditForm(prev => {
      // If trying to unselect the only selected room, don't allow it
      if (prev.room_ids.includes(roomId) && prev.room_ids.length === 1) {
        return prev;
      }
      
      // Clear room error when user selects a room
      if (errors.room_ids) {
        setErrors(prev => ({ ...prev, room_ids: '' }));
      }
      
      return {
        ...prev,
        room_ids: prev.room_ids.includes(roomId)
          ? prev.room_ids.filter(id => id !== roomId)
          : [...prev.room_ids, roomId]
      };
    });
  };

  const handleSaveChanges = async () => {
    // Validate title is not empty
    if (!editForm.title.trim()) {
      setErrors(prev => ({ ...prev, title: isAr ? 'العنوان مطلوب' : 'Title is required' }));
      return;
    }
    
    // Validate at least one room is selected
    if (editForm.room_ids.length === 0) {
      setErrors(prev => ({ ...prev, room_ids: isAr ? 'يجب اختيار فصل واحد على الأقل' : 'At least one room must be selected' }));
      return;
    }
    
    setSaving(true);
    try {
      const formData = new FormData();
      
      // Only send changed fields
      if (editForm.title !== originalValues.title) {
        formData.append('title', editForm.title);
      }
      
      if (editForm.description !== originalValues.description) {
        formData.append('description', editForm.description);
      }
      
      if (editForm.target_audience !== originalValues.target_audience) {
        formData.append('target_audience', editForm.target_audience);
      }
      
      // Check if room_ids changed
      const roomsChanged = JSON.stringify([...editForm.room_ids].sort()) !== 
                          JSON.stringify([...originalValues.room_ids].sort());
      
      if (roomsChanged) {
        editForm.room_ids.forEach(roomId => {
          formData.append('rooms', roomId);
        });
      }
      
      // Add new files
      editForm.newFiles.forEach(file => {
        formData.append('files', file);
      });

      // Only make API call if there are changes
      const hasChanges = editForm.title !== originalValues.title ||
                        editForm.description !== originalValues.description ||
                        editForm.target_audience !== originalValues.target_audience ||
                        roomsChanged ||
                        editForm.newFiles.length > 0;

      if (hasChanges) {
        await axiosPrivate.patch(API_URLS.updateDocumentGroup(documentId), formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      // Refresh document data
      const res = await axiosPrivate.get(API_URLS.getDocumentGroupDetails(documentId));
      setDocument(res.data);
      
      // Update original values
      setOriginalValues({
        title: res.data.title,
        description: res.data.description || '',
        target_audience: res.data.target_audience,
        room_ids: res.data.rooms || []
      });
      
      setEditForm({
        title: res.data.title,
        description: res.data.description || '',
        target_audience: res.data.target_audience,
        room_ids: res.data.rooms || [],
        newFiles: []
      });
      
      setIsEditMode(false);
      toast.success(isAr ? 'تم الحفظ بنجاح' : 'Changes saved successfully');
      
      if (onEdit) {
        onEdit(res.data);
      }
    } catch (error) {
      toast.error(isAr ? 'حدث خطأ أثناء الحفظ' : 'Error saving changes');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (deleteType === 'group') {
      onDelete(document);
      setShowDeleteConfirm(false);
      onClose();
    } else if (deleteType === 'file') {
      // Delete file without reloading
      setDeletingFileId(fileToDeleteId);
      try {
        await axiosPrivate.delete(API_URLS.removeDocumentFile(documentId, fileToDeleteId));
        
        // Update document state locally - remove the deleted file
        setDocument(prev => ({
          ...prev,
          documents: prev.documents.filter(file => file.id !== fileToDeleteId),
          document_count: prev.document_count - 1
        }));
        
        toast.success(isAr ? 'تم حذف الملف بنجاح' : 'File deleted successfully');
      } catch (error) {
        console.error(error);
        toast.error(isAr ? 'حدث خطأ أثناء حذف الملف' : 'Error deleting file');
      } finally {
        setDeletingFileId(null);
        setShowDeleteConfirm(false);
        setDeleteType(null);
        setFileToDeleteId(null);
      }
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setDeleteType(null);
    setFileToDeleteId(null);
  };

  if (!open) return null;

  return (
    <div 
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.4)', 
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300,
        padding: '20px'
      }}
      onClick={(e) => {
        // Only close if clicking the backdrop AND no delete dialog is open
        if (e.target === e.currentTarget && !showDeleteConfirm) {
          onClose();
        }
      }}
    >
      <div 
        style={{
          width: '100%', maxWidth: '750px', 
          maxHeight: '90vh',
          backgroundColor: '#fff', borderRadius: '30px', 
          boxShadow: '0 25px 50px rgba(0,0,0,0.15)',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden', direction: isAr ? 'rtl' : 'ltr',
          border: '1px solid #f0f0f0'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 d-flex justify-content-between align-items-center border-bottom shadow-sm" style={{ backgroundColor: '#fff', zIndex: 2 }}>
          <h5 className="mb-0 fw-bold" style={{ color: THEME.textMain }}>
            {isEditMode ? (isAr ? 'تعديل المستند' : 'Edit Document') : (document?.title || (isAr ? 'التفاصيل' : 'Details'))}
          </h5>
          <button onClick={onClose} className="btn btn-light rounded-circle" style={{ width: '36px', height: '36px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><BiX size={20} /></button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-auto custom-scrollbar" style={{ flex: 1 }}>
          {loading ? (
            <div className="text-center py-5"><div className="spinner-border spinner-border-sm" style={{ color: THEME.primary }}></div></div>
          ) : document ? (
            <>
              {isEditMode ? (
                // Edit Mode
                <>
                  {/* Title */}
                  <div className="mb-3">
                    <label className="form-label small fw-bold" style={{ color: THEME.textMuted }}>
                      {isAr ? 'العنوان' : 'Title'}
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={editForm.title}
                      onChange={(e) => {
                        setEditForm(prev => ({ ...prev, title: e.target.value }));
                        if (errors.title) {
                          setErrors(prev => ({ ...prev, title: '' }));
                        }
                      }}
                      style={{ 
                        borderRadius: '12px', 
                        border: errors.title ? '1px solid #dc3545' : '1px solid #e0e0e0' 
                      }}
                      required
                    />
                    {errors.title && (
                      <div className="text-danger small mt-1" style={{ fontSize: '0.85rem' }}>
                        {errors.title}
                      </div>
                    )}
                  </div>

                  {/* Description */}
                  <div className="mb-3">
                    <label className="form-label small fw-bold" style={{ color: THEME.textMuted }}>
                      {isAr ? 'الوصف' : 'Description'}
                    </label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={editForm.description}
                      onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                      style={{ borderRadius: '12px', border: '1px solid #e0e0e0' }}
                    />
                  </div>

                  {/* Target Audience */}
                  <div className="mb-3">
                    <label className="form-label small fw-bold" style={{ color: THEME.textMuted }}>
                      {isAr ? 'الجمهور المستهدف' : 'Target Audience'}
                    </label>
                    <select
                      className="form-select"
                      value={editForm.target_audience}
                      onChange={(e) => setEditForm(prev => ({ ...prev, target_audience: e.target.value }))}
                      style={{ borderRadius: '12px', border: '1px solid #e0e0e0' }}
                    >
                      <option value="staff">{t('document_model.staff')}</option>
                      <option value="parents">{t('document_model.parents')}</option>
                    </select>
                  </div>

                  {/* Rooms */}
                  <div className="mb-4 p-3 rounded-4" style={{ backgroundColor: THEME.softBg, border: '1px solid #f0f0f0' }}>
                    <p className="small fw-bold mb-2 text-uppercase" style={{ color: THEME.primary, fontSize: '0.7rem' }}>
                      <BiLayer className="me-1 ms-1" /> {isAr ? 'الفصول المخصصة' : 'Assigned Rooms'}
                    </p>
                    <div className="d-flex flex-wrap gap-2">
                      {availableRooms.length > 0 ? availableRooms.map((room) => {
                        const isSelected = editForm.room_ids.includes(room.id);
                        const isOnlySelected = isSelected && editForm.room_ids.length === 1;
                        
                        return (
                          <span
                            key={room.id}
                            className={`px-3 py-1 rounded-pill small fw-medium shadow-sm`}
                            style={{
                              backgroundColor: isSelected ? THEME.primary : '#fff',
                              color: isSelected ? '#fff' : THEME.textMuted,
                              border: '1px solid #e0e0e0',
                              cursor: isOnlySelected ? 'not-allowed' : 'pointer',
                              opacity: isOnlySelected ? 0.7 : 1,
                              transition: 'all 0.2s'
                            }}
                            onClick={() => handleRoomToggle(room.id)}
                            title={isOnlySelected ? (isAr ? 'لا يمكن إلغاء تحديد الفصل الوحيد' : 'Cannot unselect the only room') : ''}
                          >
                            {room.name}
                          </span>
                        );
                      }) : (
                        <span className="text-muted small">{isAr ? 'لا توجد فصول متاحة' : 'No rooms available'}</span>
                      )}
                    </div>
                    {errors.room_ids && (
                      <div className="text-danger small mt-2" style={{ fontSize: '0.85rem' }}>
                        {errors.room_ids}
                      </div>
                    )}
                  </div>

                  {/* Existing Files */}
                  {document.documents && document.documents.length > 0 && (
                    <div className="mb-3">
                      <p className="small fw-bold text-muted mb-2 d-flex align-items-center gap-2">
                        <BiFile /> {isAr ? 'الملفات الحالية' : 'Current Files'}
                      </p>
                      <div className="d-flex flex-column gap-2">
                        {document.documents.map((f) => (
                          <div
                            key={f.id}
                            className="d-flex align-items-center p-2 px-3 rounded-4 border"
                            style={{ 
                              backgroundColor: '#fff',
                              transition: 'background-color 0.2s',
                              opacity: deletingFileId === f.id ? 0.5 : 1
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fff'}
                          >
                            <BiFile size={20} color={THEME.primary} className="me-2 ms-2" />
                            <div className="flex-grow-1 small fw-medium text-truncate">{f.file_name}</div>
                            <div className="d-flex gap-1">
                              <button 
                                className="btn btn-sm btn-light" 
                                onClick={() => window.open(f.file_url || f.file, '_blank')}
                                disabled={deletingFileId === f.id}
                                style={{ width: '32px', height: '32px', borderRadius: '50%', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                              >
                                <BiShow size={16} />
                              </button>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => triggerDeleteFile(f.id)}
                                disabled={document.documents.length <= 1 || deletingFileId === f.id}
                                style={{ 
                                  width: '32px', 
                                  height: '32px', 
                                  borderRadius: '50%', 
                                  padding: 0, 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  justifyContent: 'center',
                                  opacity: document.documents.length <= 1 ? 0.5 : 1,
                                  cursor: (document.documents.length <= 1 || deletingFileId === f.id) ? 'not-allowed' : 'pointer'
                                }}
                              >
                                {deletingFileId === f.id ? (
                                  <span className="spinner-border spinner-border-sm"></span>
                                ) : (
                                  <BiTrash size={14} />
                                )}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* New Files */}
                  <div className="mb-3">
                    <p className="small fw-bold text-muted mb-2 d-flex align-items-center gap-2">
                      <BiUpload /> {isAr ? 'إضافة ملفات جديدة' : 'Add New Files'}
                    </p>
                    
                    <input
                      type="file"
                      multiple
                      ref={fileInputRef}
                      className="d-none"
                      onChange={handleFileSelect}
                    />

                    <button
                      type="button"
                      className="btn w-100 d-flex align-items-center justify-content-between px-3 py-2"
                      style={{ 
                        borderRadius: '12px', 
                        border: '1px dashed #ced4da', 
                        backgroundColor: '#fff',
                        color: '#6c757d'
                      }}
                      onClick={() => fileInputRef.current.click()}
                    >
                      <span className="small">
                        {isAr ? 'اختر الملفات...' : 'Choose files...'}
                      </span>
                      <BiUpload size={18} />
                    </button>

                    {editForm.newFiles.length > 0 && (
                      <div className="d-flex flex-column gap-2 mt-2">
                        {editForm.newFiles.map((file, idx) => (
                          <div key={idx} className="d-flex align-items-center p-2 px-3 rounded-4 border" style={{ backgroundColor: '#f8f9fa' }}>
                            <BiFile size={20} color={THEME.primary} className="me-2 ms-2" />
                            <div className="flex-grow-1 small fw-medium text-truncate">{file.name}</div>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleRemoveNewFile(idx)}
                              style={{ width: '32px', height: '32px', borderRadius: '50%', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                              <BiX size={16} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                // View Mode
                <>
                  {document.description && (
                    <div className="mb-4">
                      <p style={{ 
                        fontSize: '0.95rem', lineHeight: '1.7', color: THEME.textMuted,
                        whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 
                      }}>
                        {document.description}
                      </p>
                    </div>
                  )}

                  <div className="d-flex gap-4 mb-4 text-muted small border-top pt-3">
                    <span className="d-flex align-items-center gap-1"><BiGroup size={17} color={THEME.primary}/> {document.target_audience == 'staff' ? (isAr ? 'الموظفين' : 'Staff') : (isAr ? 'اولياء الامور' : 'Parents')}</span>
                    <span className="d-flex align-items-center gap-1"><BiCalendar size={17} color={THEME.primary}/> {new Date(document.created_at).toLocaleDateString()}</span>
                  </div>

                  <div className="mb-4 p-3 rounded-4" style={{ backgroundColor: THEME.softBg, border: '1px solid #f0f0f0' }}>
                    <p className="small fw-bold mb-2 text-uppercase" style={{ color: THEME.primary, fontSize: '0.7rem' }}>
                      <BiLayer className="me-1 ms-1" /> {isAr ? 'الفصول المخصصة' : 'Assigned Rooms'}
                    </p>
                    <div className="d-flex flex-wrap gap-2">
                      {document.room_names?.length > 0 ? document.room_names.map((r, i) => (
                        <span key={i} className="bg-white border px-3 py-1 rounded-pill small fw-medium shadow-sm">
                          {r}
                        </span>
                      )) : <span className="text-muted small">No rooms assigned</span>}
                    </div>
                  </div>

                  <div className="mb-2">
                    <p className="small fw-bold text-muted mb-3 d-flex align-items-center gap-2">
                      <BiFile /> {isAr ? 'الملفات المرفقة' : 'Attached Files'}
                    </p>
                    <div className="d-flex flex-column gap-2">
                      {document.documents?.map((f, i) => (
                        <div 
                          key={i} 
                          className="d-flex align-items-center p-2 px-3 rounded-4 border shadow-none" 
                          style={{ 
                            backgroundColor: '#fff',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fff'}
                        >
                          <BiFile size={20} color={THEME.primary} className="me-2 ms-2" />
                          <div className="flex-grow-1 small fw-medium text-truncate">{f.file_name}</div>
                          <div className="d-flex gap-1">
                            <button 
                              className="btn btn-sm btn-light" 
                              onClick={() => window.open(f.file_url || f.file, '_blank')}
                              style={{ width: '32px', height: '32px', borderRadius: '50%', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                              <BiShow size={16} />
                            </button>
                            <button 
                              className="btn btn-sm btn-light" 
                              style={{ width: '32px', height: '32px', borderRadius: '50%', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: THEME.primary }} 
                              onClick={() => handleDownload(f.file_url || f.file, f.file_name)}
                            >
                              <BiDownload size={16} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </>
          ) : null}
        </div>

        {/* Footer */}
        <div className="p-3 border-top bg-light d-flex gap-2" style={{ zIndex: 2 }}>
          {isEditMode ? (
            <>
              <button 
                className="btn flex-grow-1 py-2 fw-bold" 
                style={{ backgroundColor: THEME.primary, color: '#fff', borderRadius: '16px', border: 'none', fontSize: '0.9rem' }}
                onClick={handleSaveChanges}
                disabled={saving}
              >
                {saving ? (
                  <span className="spinner-border spinner-border-sm me-2 ms-2"></span>
                ) : (
                  <BiSave className="me-2 ms-2" />
                )}
                {saving ? (isAr ? 'جاري الحفظ...' : 'Saving...') : (isAr ? 'حفظ التغييرات' : 'Save Changes')}
              </button>
              <button 
                className="btn btn-outline-secondary py-2" 
                style={{ borderRadius: '16px', width: '48px', height: '48px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                onClick={handleCancelEdit}
                disabled={saving}
              >
                <BiXCircle size={20} />
              </button>
            </>
          ) : (
            <>
              <button 
                className="btn flex-grow-1 py-2 fw-bold" 
                style={{ backgroundColor: THEME.primary, color: '#fff', borderRadius: '16px', border: 'none', fontSize: '0.9rem' }}
                onClick={handleEditClick}
              >
                <BiEdit className="me-2 ms-2" /> {isAr ? 'تعديل البيانات' : 'Edit Document'}
              </button>
              <button 
                className="btn btn-outline-danger py-2" 
                style={{ borderRadius: '16px', border: '1px solid #ff000033', width: '48px', height: '48px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                onClick={handleDeleteGroup}
              >
                <BiTrash size={20} />
              </button>
             
            </>
          )}
        </div>
      </div>
       {showDeleteConfirm && (
                <DeleteConfirmationDialog 
                    onDelete={handleConfirmDelete} 
                    onCancel={() => setShowDeleteConfirm(false)}
                />
                )}
    </div>
    
  );
};